import {
  buildSearchProjection,
  decryptRecordPayload,
  encryptRecordPayload,
  isCollectionRecord,
  isMemoryCard,
  normalizeSearchText,
  type CollectionRecord,
  type MaterializedRecord,
  type MemoryCard,
  type RecordKind,
  type RecordPayload,
  type ReminderSpec,
} from '@safewallet/core';
import type { PowerSyncDatabase } from '@powersync/web';

interface RevisionRow {
  id: string;
  vault_id: string;
  record_id: string;
  record_kind: RecordKind;
  base_revision_id: string | null;
  operation_id: string;
  device_id: string;
  crypto_version: number;
  wrapped_record_key: string;
  wrapped_record_key_nonce: string;
  nonce: string;
  ciphertext: string;
  client_created_at: string;
  received_at: string | null;
  revision_state: 'pending' | 'current' | 'branch' | 'superseded';
  deleted: number;
  resolves_conflict: number;
}

interface RecordRow {
  id: string;
  vault_id: string;
  record_kind: RecordKind;
  current_revision_id: string | null;
  conflict_state: 'clean' | 'conflicted';
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface VaultSnapshot {
  cards: Array<MaterializedRecord<MemoryCard>>;
  collections: Array<MaterializedRecord<CollectionRecord>>;
  conflictRecords: Array<MaterializedRecord<RecordPayload>>;
}

function reminderDueAt(reminder: ReminderSpec): string {
  const time = reminder.localTime || '09:00';
  const [year, month, day] = reminder.dueDate.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes).toISOString();
}

function encryptedEnvelope(row: RevisionRow) {
  return {
    cryptoVersion: 1 as const,
    wrappedRecordKey: row.wrapped_record_key,
    wrappedRecordKeyNonce: row.wrapped_record_key_nonce,
    nonce: row.nonce,
    ciphertext: row.ciphertext,
  };
}

export class VaultRepository {
  constructor(
    private readonly db: PowerSyncDatabase,
    readonly vaultId: string,
    readonly deviceId: string,
  ) {}

  async savePayload(
    payload: RecordPayload,
    recordKind: RecordKind,
    vaultRootKey: Uint8Array,
    baseRevisionId: string | null,
    options: { deleted?: boolean; resolvesConflict?: boolean } = {},
  ): Promise<string> {
    const revisionId = crypto.randomUUID();
    const operationId = crypto.randomUUID();
    const createdAt = new Date().toISOString();
    const encrypted = await encryptRecordPayload(payload, vaultRootKey, recordKind, revisionId);

    await this.db.writeTransaction(async (tx) => {
      await tx.execute(
        `INSERT INTO record_revisions (
          id, vault_id, record_id, record_kind, base_revision_id, operation_id, device_id,
          crypto_version, wrapped_record_key, wrapped_record_key_nonce, nonce, ciphertext,
          client_created_at, received_at, revision_state, deleted, resolves_conflict
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          revisionId,
          this.vaultId,
          payload.recordId,
          recordKind,
          baseRevisionId,
          operationId,
          this.deviceId,
          encrypted.cryptoVersion,
          encrypted.wrappedRecordKey,
          encrypted.wrappedRecordKeyNonce,
          encrypted.nonce,
          encrypted.ciphertext,
          createdAt,
          null,
          'pending',
          options.deleted ? 1 : 0,
          options.resolvesConflict ? 1 : 0,
        ],
      );

      if (recordKind === 'card' && isMemoryCard(payload)) {
        if (payload.reminder && !options.deleted) {
          const reminder = payload.reminder;
          await tx.execute(
            `INSERT OR REPLACE INTO reminders (
              id, vault_id, record_id, source_revision_id, due_at, due_date, local_time, timezone,
              all_day, recurrence, mode, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              payload.recordId,
              this.vaultId,
              payload.recordId,
              revisionId,
              reminderDueAt(reminder),
              reminder.dueDate,
              reminder.localTime ?? null,
              reminder.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
              reminder.allDay ? 1 : 0,
              reminder.recurrence ?? 'none',
              reminder.mode,
              createdAt,
            ],
          );
        } else {
          await tx.execute('DELETE FROM reminders WHERE id = ?', [payload.recordId]);
        }
      }
    });

    return revisionId;
  }

  async loadSnapshot(vaultRootKey: Uint8Array): Promise<VaultSnapshot> {
    const [revisionRows, recordRows] = await Promise.all([
      this.db.getAll<RevisionRow>(
        'SELECT * FROM record_revisions WHERE vault_id = ? ORDER BY client_created_at ASC',
        [this.vaultId],
      ),
      this.db.getAll<RecordRow>('SELECT * FROM records WHERE vault_id = ?', [this.vaultId]),
    ]);

    const records = new Map(recordRows.map((row) => [row.id, row]));
    const revisionsByRecord = new Map<string, RevisionRow[]>();
    for (const row of revisionRows) {
      const existing = revisionsByRecord.get(row.record_id) ?? [];
      existing.push(row);
      revisionsByRecord.set(row.record_id, existing);
    }

    const cards: Array<MaterializedRecord<MemoryCard>> = [];
    const collections: Array<MaterializedRecord<CollectionRecord>> = [];
    const conflictRecords: Array<MaterializedRecord<RecordPayload>> = [];
    const locallyResolvedRecordIds = new Set(
      revisionRows
        .filter((row) => row.revision_state === 'pending' && row.device_id === this.deviceId && row.resolves_conflict === 1)
        .map((row) => row.record_id),
    );

    for (const [recordId, revisions] of revisionsByRecord) {
      const record = records.get(recordId);
      const pending = [...revisions]
        .reverse()
        .find((row) => row.revision_state === 'pending' && row.device_id === this.deviceId);
      const canonical = record?.current_revision_id
        ? revisions.find((row) => row.id === record.current_revision_id)
        : undefined;
      const fallback = [...revisions]
        .reverse()
        .find((row) => row.revision_state !== 'branch' && row.revision_state !== 'superseded');
      const selected = pending ?? canonical ?? fallback ?? revisions.at(-1);
      if (!selected) continue;

      try {
        const payload = await decryptRecordPayload<RecordPayload>(
          encryptedEnvelope(selected),
          vaultRootKey,
          selected.record_id,
          selected.record_kind,
          selected.id,
        );
        const conflicted = record?.conflict_state === 'conflicted' && !locallyResolvedRecordIds.has(recordId);
        const materialized = {
          recordId,
          revisionId: selected.id,
          baseRevisionId: selected.base_revision_id,
          recordKind: selected.record_kind,
          payload,
          pending: selected.revision_state === 'pending',
          conflicted,
          deleted: selected.deleted === 1,
        } satisfies MaterializedRecord;

        if (conflicted) conflictRecords.push(materialized);
        if (materialized.deleted) continue;

        if (selected.record_kind === 'card' && isMemoryCard(payload)) {
          cards.push(materialized as MaterializedRecord<MemoryCard>);
        }
        if (selected.record_kind === 'collection' && isCollectionRecord(payload)) {
          collections.push(materialized as MaterializedRecord<CollectionRecord>);
        }
      } catch (error) {
        console.error(`Could not decrypt record ${recordId}.`, error);
      }
    }

    cards.sort((a, b) => b.payload.clientMetadata.updatedAt.localeCompare(a.payload.clientMetadata.updatedAt));
    collections.sort((a, b) => a.payload.name.localeCompare(b.payload.name));
    conflictRecords.sort((a, b) => {
      const aUpdated = isMemoryCard(a.payload) ? a.payload.clientMetadata.updatedAt : a.payload.updatedAt;
      const bUpdated = isMemoryCard(b.payload) ? b.payload.clientMetadata.updatedAt : b.payload.updatedAt;
      return bUpdated.localeCompare(aUpdated);
    });
    await this.rebuildSearchProjection(cards, collections);
    return { cards, collections, conflictRecords };
  }

  private async rebuildSearchProjection(
    cards: Array<MaterializedRecord<MemoryCard>>,
    collections: Array<MaterializedRecord<CollectionRecord>>,
  ): Promise<void> {
    const collectionMap = new Map(collections.map((entry) => [entry.recordId, entry.payload]));
    const updatedAt = new Date().toISOString();
    await this.db.writeTransaction(async (tx) => {
      await tx.execute('DELETE FROM search_projection WHERE vault_id = ?', [this.vaultId]);
      for (const card of cards) {
        const projection = buildSearchProjection(card.payload, collectionMap);
        await tx.execute(
          'INSERT OR REPLACE INTO search_projection (id, vault_id, text, updated_at) VALUES (?, ?, ?, ?)',
          [card.recordId, this.vaultId, projection.text, updatedAt],
        );
      }
    });
  }

  async clearSearchProjection(): Promise<void> {
    await this.db.execute('DELETE FROM search_projection WHERE vault_id = ?', [this.vaultId]);
  }

  async searchRecordIds(query: string): Promise<Set<string>> {
    const terms = normalizeSearchText(query).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return new Set();
    const where = terms.map(() => 'text LIKE ?').join(' AND ');
    const rows = await this.db.getAll<{ id: string }>(
      `SELECT id FROM search_projection WHERE vault_id = ? AND ${where}`,
      [this.vaultId, ...terms.map((term) => `%${term}%`)],
    );
    return new Set(rows.map((row) => row.id));
  }

  async resolveConflict(
    recordId: string,
    payload: RecordPayload,
    recordKind: RecordKind,
    vaultRootKey: Uint8Array,
    deleted: boolean,
  ): Promise<string> {
    const record = await this.db.getOptional<RecordRow>(
      'SELECT * FROM records WHERE vault_id = ? AND id = ?',
      [this.vaultId, recordId],
    );
    if (!record?.current_revision_id) throw new Error('The canonical conflict version is not available yet.');
    return this.savePayload(payload, recordKind, vaultRootKey, record.current_revision_id, {
      deleted,
      resolvesConflict: true,
    });
  }

  async loadConflictBranches(
    recordId: string,
    vaultRootKey: Uint8Array,
  ): Promise<Array<MaterializedRecord<RecordPayload>>> {
    const rows = await this.db.getAll<RevisionRow>(
      `SELECT * FROM record_revisions
       WHERE vault_id = ? AND record_id = ? AND revision_state IN ('current', 'branch')
       ORDER BY client_created_at DESC`,
      [this.vaultId, recordId],
    );
    const output: Array<MaterializedRecord<RecordPayload>> = [];
    for (const row of rows) {
      const payload = await decryptRecordPayload<RecordPayload>(
        encryptedEnvelope(row),
        vaultRootKey,
        row.record_id,
        row.record_kind,
        row.id,
      );
      output.push({
        recordId: row.record_id,
        revisionId: row.id,
        baseRevisionId: row.base_revision_id,
        recordKind: row.record_kind,
        payload,
        pending: false,
        conflicted: true,
        deleted: row.deleted === 1,
      });
    }
    return output;
  }

  watchChanges(onChange: () => void): () => void {
    const controller = new AbortController();
    void (async () => {
      try {
        for await (const _result of this.db.watch(
          `SELECT
             (SELECT count(*) FROM record_revisions WHERE vault_id = ?) AS revision_count,
             (SELECT count(*) FROM records WHERE vault_id = ?) AS record_count`,
          [this.vaultId, this.vaultId],
          { signal: controller.signal },
        )) {
          onChange();
        }
      } catch (error) {
        if (!controller.signal.aborted) console.error('Vault change watcher failed.', error);
      }
    })();
    return () => controller.abort();
  }
}
